/**
 * Lógica pura de las rotaciones de turnos (ciclos de N días, p. ej. el catorcenal de 14 días).
 * Contrato: diseno-rotaciones-turnos.md / PRD "Rotaciones de turnos" de ControlAccesoAPI.
 *
 * Una rotación del catálogo es una secuencia de N días; cada día es un turno o un descanso
 * (`IsRest`), nunca ninguno de los dos. Las horas siguen saliendo del WorkShift referenciado, así
 * que aquí solo se mapea la estructura (el front pinta cada día con los datos del turno que ya
 * tiene cargados en "Configuración de horario de trabajo por proyecto").
 */

/** Longitud del ciclo catorcenal — el caso pedido por negocio (el backend admite 2…56). */
export const BIWEEKLY_CYCLE_DAYS = 14;

/** Días en un lado del ciclo: la grilla y las tarjetas pintan una semana (7 días) por fila. */
export const DAYS_PER_ROW = 7;

export interface RotationDay {
  /** Posición dentro del ciclo, 0…N−1 (el día 0 cae en el ancla de la asignación). */
  dayIndex: number;
  workShiftId: string | null;
  isRest: boolean;
  workShiftName: string | null;
  /** Vigencia del turno referenciado; si ya venció, los días del ciclo quedan "sin programar". */
  workShiftEndDate: string | null;
}

/** Rotación del catálogo, ya con su última versión aplanada. */
export interface WorkShiftRotation {
  id: string;
  name: string;
  isActive: boolean;
  companyInfoId: string | null;
  projectId: string | null;
  versionNumber: number;
  cycleLengthDays: number;
  days: RotationDay[];
  /** Último día de vigencia del patrón ("YYYY-MM-DD"). Nulo = "Repetir indefinidamente": sin límite
   * de tiempo. */
  endDate: string | null;
  /** Derivado de `endDate`: true cuando el patrón no tiene fecha fin. */
  repeatIndefinitely: boolean;
  /** Contratos que tienen asignada esta rotación (cualquier versión). */
  assignedContracts: number;
}

/** Asignación de una rotación a un contrato — viene en la raíz `Rotations` del GET Schedule. */
export interface RotationAssignment {
  id: number;
  workShiftRotationId: string;
  workShiftRotationVersionId: string | null;
  versionNumber: number | null;
  latestVersionNumber: number | null;
  name: string;
  anchorDate: string;
  startDate: string;
  /** Nulo = indefinida ("Repetir indefinidamente"). */
  endDate: string | null;
}

export interface RotationWarning {
  Code: string;
  Message?: string;
}

/** Origen de cada día resuelto por `resolve_workshift` (GET Schedule, minuta, masiva). */
export type ScheduleSource = 'INDIVIDUAL' | 'ROTATION' | 'CONTRACT';

export interface AssignRotationBody {
  workShiftRotationId: string;
  startDate: string;
  /** Nulo/ausente = indefinida. */
  endDate?: string | null;
  /** Por defecto = startDate; permite retomar la rotación conservando su fase. */
  anchorDate?: string | null;
}

export interface RotationSaveBody {
  name: string;
  companyInfoId: string;
  projectId: string | null;
  cycleLengthDays: number;
  days: Array<{ dayIndex: number; workShiftId: string | null; isRest: boolean }>;
  /** Nulo cuando "Repetir indefinidamente" está encendido; si no, la fecha fin ("YYYY-MM-DD"). */
  endDate: string | null;
}

/** Los turnos usan 3000-12-31 como "sin fin"; el backend de rotaciones usa nulo. Ambos = indefinido. */
export function normalizeEndDate(value: any): string | null {
  if (!value) { return null; }
  const key = String(value).substring(0, 10);
  return Number(key.substring(0, 4)) >= 3000 ? null : key;
}

function pick<T>(...values: T[]): T | undefined {
  return values.find(v => v !== undefined && v !== null);
}

/**
 * Normaliza una rotación del catálogo. El detalle de la última versión puede llegar aplanado en
 * la propia rotación o anidado (`LatestVersion`/`Version`); se aceptan las dos formas para no
 * depender de cómo el backend la serialice.
 */
export function mapRotationFromApi(raw: any): WorkShiftRotation {
  const version = pick(raw?.LatestVersion, raw?.Version, raw);
  // La vigencia puede venir en la rotación o dentro de su última versión.
  const endDate = normalizeEndDate(pick(raw?.EndDate, version?.EndDate));
  const rawDays: any[] = pick(version?.Days, raw?.Days) || [];
  const days: RotationDay[] = rawDays
    .map(d => ({
      dayIndex: Number(d.DayIndex),
      workShiftId: d.WorkShiftId ?? null,
      isRest: !!d.IsRest,
      workShiftName: d.WorkShiftName ?? null,
      workShiftEndDate: d.WorkShiftEndDate ?? null
    }))
    .sort((a, b) => a.dayIndex - b.dayIndex);
  return {
    id: raw.Id,
    name: raw.Name || '',
    isActive: raw.IsActive !== false,
    companyInfoId: raw.CompanyInfoId ?? null,
    projectId: raw.ProjectId ?? null,
    versionNumber: Number(pick(version?.VersionNumber, raw?.VersionNumber, 1)),
    cycleLengthDays: Number(pick(version?.CycleLengthDays, raw?.CycleLengthDays, days.length)),
    days,
    endDate,
    repeatIndefinitely: endDate === null,
    assignedContracts: Number(raw.AssignedContracts ?? 0)
  };
}

export function mapRotationAssignmentFromApi(raw: any): RotationAssignment {
  return {
    id: raw.Id,
    workShiftRotationId: raw.WorkShiftRotationId,
    workShiftRotationVersionId: raw.WorkShiftRotationVersionId ?? null,
    versionNumber: raw.VersionNumber ?? null,
    latestVersionNumber: raw.LatestVersionNumber ?? null,
    name: raw.Name || '',
    anchorDate: String(raw.AnchorDate || '').substring(0, 10),
    startDate: String(raw.StartDate || '').substring(0, 10),
    endDate: raw.EndDate ? String(raw.EndDate).substring(0, 10) : null
  };
}

/** Avisos no bloqueantes que el backend adjunta al guardar una rotación o una asignación. */
export function extractRotationWarnings(resp: any): RotationWarning[] {
  const warnings = resp?.Warnings || resp?.warnings || [];
  return Array.isArray(warnings) ? warnings : [];
}

/** Texto para el usuario de cada aviso conocido; los desconocidos se muestran con su Message. */
export function describeRotationWarning(warning: RotationWarning, assignedContracts?: number): string {
  switch (warning.Code) {
    case 'CONTRACTS_KEEP_PREVIOUS_VERSION': {
      const who = assignedContracts ? `Los ${assignedContracts} contratos que la tienen asignada` : 'Los contratos que ya la tienen asignada';
      return `${who} siguen con la versión anterior hasta que se les vuelva a asignar la rotación.`;
    }
    case 'WORKSHIFT_HAS_END_DATE':
      return 'Algún turno de la rotación tiene fecha de fin: desde esa fecha esos días quedarán sin programar.';
    case 'ROTATION_LENGTH_CHANGED':
      return 'Cambiar la longitud del ciclo desplaza la fase de los contratos que ya la tienen asignada.';
    case 'UNSCHEDULED_DAYS':
      return warning.Message || 'Hay días sin turno vigente.';
    default:
      return warning.Message || warning.Code;
  }
}

/** Fecha "YYYY-MM-DD" + `days` días (en hora local, sin saltos por zona horaria). */
export function addDaysToDateKey(dateKeyValue: string, days: number): string {
  const [y, m, d] = dateKeyValue.split('-').map(Number);
  const date = new Date(y, m - 1, d + days);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

/** Día del ciclo de una fecha: (fecha − ancla) mod N, contado en días corridos (RF4). */
export function cycleDayFor(dateKeyValue: string, anchorDateKey: string, cycleLengthDays: number): number {
  const toUtcDays = (key: string) => {
    const [y, m, d] = key.split('-').map(Number);
    return Math.floor(Date.UTC(y, m - 1, d) / 86400000);
  };
  const diff = toUtcDays(dateKeyValue) - toUtcDays(anchorDateKey);
  return ((diff % cycleLengthDays) + cycleLengthDays) % cycleLengthDays;
}
